// components/AuthComponents/steps/phone-number.tsx
"use client";

import { useState, useEffect, useTransition, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import PhoneInput from "@/components/AuthComponents/AuthUI/phone-number-input";
import { useActionState } from "react";
import { sendPhoneOtpAction } from "@/app/actions/phone-verification";
import type { AuthActionResponse } from "@/app/actions/phone-verification";
import Link from "next/link";
import { useTranslations } from "next-intl";
// réponse standard de l’action (imported shared type)

interface PhoneNumberProps {
  onSubmitAction: (phoneNumber: string, verificationId: string) => void;
  onErrorAction: (error: string) => void;
  onBackAction: () => void;
  onSkipAction?: () => void; // Nouvelle prop optionnelle
}

export default function PhoneNumber({
  onSubmitAction,
  onErrorAction,
  onBackAction,
  onSkipAction,
}: PhoneNumberProps) {
  const t = useTranslations("auth");
  const tCommon = useTranslations("common");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [localError, setLocalError] = useState("");
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!phoneNumber) {
      setPhoneError(null);
      setCheckingPhone(false);
      return;
    }

    const digits = phoneNumber.replace(/\D/g, "");
    if (digits.length < 9) {
      setPhoneError(null);
      setCheckingPhone(false);
      return;
    }

    setCheckingPhone(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/check-phone-availability?phone=${encodeURIComponent(phoneNumber)}`
        );
        const json = await res.json();
        if (!res.ok) {
          if (res.status === 400) {
            setPhoneError(null);
            return;
          }
          throw new Error(json.error);
        }
        setPhoneError(json.taken ? t("phoneTaken") : null);
      } catch {
        setPhoneError(t("errorCheckingPhone"));
      } finally {
        setCheckingPhone(false);
      }
    }, 500);
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, [phoneNumber, t]);

  // 2) Prépare l’action serveur
  const [isPending, startTransition] = useTransition();
  const initialState: AuthActionResponse = { success: false };
  const [sendPhoneState, runSendPhone] = useActionState<
    AuthActionResponse,
    FormData
  >(
    async (_prev, formData) => {
      return await sendPhoneOtpAction(formData);
    },
    initialState
  );

  // 3) Réagit à la réponse de l’action
  useEffect(() => {
    if (sendPhoneState.error) {
      onErrorAction(sendPhoneState.error);
      setLocalError(sendPhoneState.error);
    } else if (
      sendPhoneState.success &&
      sendPhoneState.data?.verificationId
    ) {
      onSubmitAction(phoneNumber, sendPhoneState.data.verificationId);
    }
  }, [sendPhoneState, phoneNumber, onErrorAction, onSubmitAction]);

  // 4) Soumission du form
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError("");

    const minLen = (countryCode.length || 0) + 4;
    if (!phoneNumber || phoneNumber.trim().length < minLen) {
      setLocalError(t("phoneIncomplete"));
      return;
    }
    if (phoneError) {
      return; // on bloque si inline check KO
    }

    const formData = new FormData();
    formData.append("phoneNumber", phoneNumber);
    startTransition(() => runSendPhone(formData));
  };

  // 5) Bouton désactivé tant que quelque condition n’est pas levée
  const disableButton =
    !phoneNumber ||
    phoneNumber.trim().length < ((countryCode.length || 0) + 4) ||
    checkingPhone ||
    Boolean(phoneError) ||
    isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pt-2 relative">
      <button
        onClick={onBackAction}
        type="button"
        className="absolute -top-1 -left-1 min-w-[44px] min-h-[44px] flex items-center justify-center p-2.5 hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors touch-manipulation"
        aria-label="Go back"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground text-center leading-tight">
        {t("addPhoneNumber")}
      </h2>
      <p className="text-center text-sm text-muted-foreground">
        {t("phoneNumberPurpose")}
      </p>

      <PhoneInput
        placeholder={t("enterPhoneNumber")}
        onChange={(value, country) => {
          setCountryCode(country.code);
          setPhoneNumber(`${country.code}${value}`);
        }}
      />

      {(localError || phoneError) && (
        <div className="p-3.5 rounded-xl bg-destructive/10 text-destructive text-sm border border-destructive/20">
          {phoneError ?? localError}
        </div>
      )}

      <Button
        type="submit"
        className="w-full min-h-[48px] py-6 text-base rounded-full touch-manipulation"
        size="lg"
        disabled={disableButton}
      >
        {isPending ? tCommon("sending") : t("sendVerificationCode")}
      </Button>

      {onSkipAction && (
        <div className="text-right">
          <button
            type="button"
            onClick={onSkipAction}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors underline"
          >
            {tCommon("skip")}
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      <p className="mt-6 text-center text-xs text-muted-foreground">
        {tCommon.rich("agreeTerms", {
          privacy: (chunks) => <Link href="/privacy" className="underline hover:text-foreground">{chunks}</Link>,
          terms: (chunks) => <Link href="/terms" className="underline hover:text-foreground">{chunks}</Link>,
        })}
      </p>
    </form>
  );
}
